import { CorrelationVector, Log, Operation as LoggingOperation, LogCategory } from '@augloop/logging/dist/src/client/logging';
import { IWebSocketWorker, WebSocketWorkerFactory } from './websocket-worker';
import { IClientMetadata, TypeCategory } from '@augloop/schemas/dist/src/client/schemas';
import { IPubSubMessage } from '@augloop/interfaces';
import { Utils } from '../utils';

class Request {
    public requestId: string;
    public correlationId: string;
    public schemaName: string;
    public data: any;
    public ended: boolean;

    private readonly callback: (data: any, error: any, end: boolean) => void;
    private readonly requestTimeoutInMs: number;
    private startTime: number;

    public constructor(requestId: string, correlationId: string, schemaName: string, data: any, callback: (data: any, error: any, end: boolean) => void, requestTimeoutInMs: number) {
        this.requestId = requestId;
        this.correlationId = correlationId;
        this.schemaName = schemaName;
        this.data = data;
        this.callback = callback;
        this.requestTimeoutInMs = requestTimeoutInMs;

        this.ended = false;
        this.startTime = 0;
    }

    public startRequest() {
        this.startTime = Utils.getCurrentTimeMs();
    }

    public getMsUntilTimeout(): number {
        const elapsed = Utils.getCurrentTimeMs() - this.startTime;
        return this.requestTimeoutInMs - elapsed;
    }

    public endRequest(error: Error) {
        if (!this.ended) {
            this.invokeCallback(null, error, true);
            this.ended = true;
        }
    }

    public addResponseData(data: any) {
        this.invokeCallback(data, null, false);
    }

    private invokeCallback(data: any, error: any, end: boolean) {
        // Create a promise to invoke the callback and ignore error to ensure that the current
        // thread won't be affected if callback throws.
        new Promise(() => this.callback(data, error, end)).catch((_reason) => { });
    }
}

export class WebSocketRequestManager {
    private requestCounter: number = 0;
    private activeRequests: Request[] = [];
    private pendingRequests: Request[] = [];
    private readonly requestTimeoutInMs: number;
    private readonly workerFactory: WebSocketWorkerFactory;
    private readonly maxActiveRequests: number;
    private worker: IWebSocketWorker;

    public constructor(requestTimeoutInMs: number, workerFactory: WebSocketWorkerFactory, maxActiveRequests: number) {
        this.requestTimeoutInMs = requestTimeoutInMs;
        this.workerFactory = workerFactory;
        this.maxActiveRequests = maxActiveRequests;
    }

    public testConnection(url: string, clientMetadata: IClientMetadata): Promise<boolean> {
        let receivedResponse = false;
        const logOp = new LoggingOperation({
            cv: (new CorrelationVector()).toString(),
            operationName: 'WebSocketTest',
            resourceId: url
        });
        logOp.success = true;
        logOp.start();
        return new Promise<boolean>(resolve => {
            const healthCheckRequest: IPubSubMessage = {
                payload: {},
                payloadSchema: { category: TypeCategory.Schema, schema: { name: 'HealthCheckRequest' } },
                requestedSchema: { category: TypeCategory.Schema, schema: { name: 'HealthCheckResponse' } },
                clientMetadata: clientMetadata
            };
            this.addRequest(url, healthCheckRequest, logOp.cv, 'HealthCheckRequest', (data: any, error: any, end: boolean) => {
                if (error) {
                    logOp.success = false;
                    logOp.resultSignature = 'Error';
                    logOp.resultDescription = error.message || error;
                }
                if (data && data.status === 'OK') {
                    receivedResponse = true;
                }
                if (end) {
                    if (logOp.success && !receivedResponse) {
                        logOp.success = false;
                        logOp.resultSignature = 'NoResponseData';
                    }
                    logOp.stop();
                    Log.info(0x2224d0d5 /* tag_8jndv */, LogCategory.CoreDefault, logOp);
                    resolve(logOp.success);
                }
            });
        });
    }

    public addRequest(url: string, data: unknown, correlationId: string, schemaName: string, callback: (data: unknown, error: Error, end: boolean) => void): void {
        const requestId = (this.requestCounter++).toString();
        const request = new Request(requestId, correlationId, schemaName, data, callback, this.requestTimeoutInMs);

        this.getWorker(url);
        this.pendingRequests.push(request);

        this.egressPendingRequests();
    }

    private egressPendingRequests(): void {
        while (this.pendingRequests.length > 0 && this.activeRequests.length < this.maxActiveRequests) {
            const request = this.pendingRequests.shift();
            this.activeRequests.push(request);

            if (this.activeRequests.length === 1) {
                const timeout = request.getMsUntilTimeout();
                setTimeout(this.onTimeout.bind(this), timeout);
            }

            request.startRequest();
            if (this.worker) {
                this.worker.egress(this.createRequestMessage(request.data, request.requestId, request.correlationId));
            }
        }
    }

    private createRequestMessage(data: any, requestId: string, correlationId: string): string {
        return JSON.stringify({ correlationId: correlationId, requestId: requestId, body: data });
    }

    private onTimeout(): void {
        // Requests in the queue are checked in order to see if they should be ended.
        while (this.activeRequests.length > 0) {
            const request = this.activeRequests[0];

            const timeout = request.getMsUntilTimeout();
            if (timeout <= 0) {
                // A timed out request will be ended and removed from the queue.
                const errorTimeOut = new Error(`Request timed out`);
                request.endRequest(errorTimeOut);
                this.activeRequests.shift();
                this.egressPendingRequests();
            } else {
                // If the top request is not timed out yet, reset the timer to its timeout.
                setTimeout(this.onTimeout.bind(this), timeout);
                return;
            }
        }
    }

    private onMessage(message: string): void {
        try {
            const parsedData = JSON.parse(message);
            for (let i = 0; i < this.activeRequests.length; i++) {
                const request = this.activeRequests[i];
                if (request.requestId === parsedData.requestId) {
                    if (parsedData.end) {
                        this.activeRequests.splice(i, 1);
                        request.endRequest(parsedData.error);
                        this.egressPendingRequests();
                    } else if (parsedData.body) {
                        request.addResponseData(parsedData.body);
                    }

                    break;
                }
            }
        } catch (err) {
            Log.error(0x2224d0d6 /* tag_8jndw */, LogCategory.CoreDefault, err);
        }
    }

    private onClose(error: string): void {
        const errorClosedConnection = new Error(`Connection closed: ${error}`);

        for (const request of this.activeRequests) {
            request.endRequest(errorClosedConnection);
        }
        this.activeRequests = [];

        for (const request of this.pendingRequests) {
            request.endRequest(errorClosedConnection);
        }
        this.pendingRequests = [];

        this.worker = undefined;
    }

    private getWorker(url: string): IWebSocketWorker {
        // Create a worker on that URL if a worker doesn't exist yet.
        // Note: the assumption here is that the URL is always the same.
        if (!this.worker) {
            this.worker = this.workerFactory();
            this.worker.init(url, this.onMessage.bind(this), this.onClose.bind(this));
        }

        return this.worker;
    }
}
